import {FAAwardWorksheetTemplate, FAStatusBy} from "types/fin-aid/fin-aid"

export enum GeneralSettingField {
    SapGpaUseFACredits = "sap_gpa_use_fa_credits",
    SapGpaType = "sap_gpa_type",
    SaPaymentCurrency = "sa_payment_currency",
    SaAllowPartialPayments = "sa_allow_partial_payments",
    SaHideGeneralPayment = "sa_hide_general_payment",
    SaShowPaymentDueAmount = "sa_show_payment_due_amount",
    SaFiscalYearStart = "sa_fiscal_year_start",
    SaFiscalYearEnd = "sa_fiscal_year_end",
    SaAcademicYearStart = "sa_acad_year_start",
    SaAcademicYearEnd = "sa_acad_year_end",
    SaStatementReportAutomated = "sa_statement_report_automated",
    SaStatementReportDay = "sa_statement_report_day",
    SaStatementReportIncludeCharges = "sa_statement_report_include_charges",
    SaStatementReportIncludePaymentPlanItems = "sa_statement_report_include_payment_plan_items",
    SaStatementReportCampusIds = "sa_statement_report_campus_ids",
    SaStatementReportTemplate = "sa_statement_report_template",
    SaDropFee = "sa_drop_fee",
    SaTuitionRefundSchedule = "tuition_refund_schedule",
    FaStatusBy = "fa_status_by",
    FaAwardWorksheetTemplate = "fa_aw_template",
    AcadActivityShowUnread = "acad_activity_show_unread",
    FaActivityShowUnread = "fa_activity_show_unread",
    AdmActivityShowUnread = "adm_activity_show_unread",
    SaActivityShowUnread = "sa_activity_show_unread",
    SsActivityShowUnread = "ss_activity_show_unread",
    ProgressReportSystemDefault = "progress_report_system_default",
    CalendarDisplayTermStartAndEndDates = "calendar_display_term_start_and_end_dates",
    CalendarDisplayProgramStartDates = "calendar_display_term_start_dates",
    CalendarDisplayProgramEndDates = "calendar_display_term_end_dates",
    ProgressReportSubmodulesBreakDown = "progress_report_submodules_break_down",
    DailyAttendanceRounding = "daily_attendance_rounding",
    MakeupAttendanceRounding = "makeup_attendance_rounding",
    MakeupDocumentAttachment = "makeup_document_attachment",
    MakeupRemote = "makeup_remote",
    MakeupDocumentAttachmentMandatory = "makeup_document_attachment_mandatory",
    AttendanceCheckBreakEvent = "attendance_check_break_event",
    DailyAttendanceTimeClockTracking = "daily_attendance_time_clock_tracking",
    AccommodatedHoursOption = "accommodated_hours_option",
    DailySpecialRoundingForCheckin = "daily_special_rounding_for_checkin",
    ClassroomSpecialRoundingForCheckin = "classroom_special_rounding_for_checkin",
    AllowedRolesInStaffsDirectory = "allowed_roles_in_staffs_directory",
    ChatThreadStyle = "chat_thread_style",
    DeleteOwnMessagesChat = "delete_own_messages_chat",
    FameGradeExportCourseGrades = "fame_grades_export_course_grades",
    FameGradeExportClassActivities = "fame_grades_export_class_activities",
    FameGradeExportPractical = "fame_grades_export_practical",
    FameGradeExportPracticalType = "fame_grades_export_practical_type",
    GradingType = "grading_type",
    LateAttendanceApproval = "late_attendance_approval",
    LateAttendanceApprovalMinutes = "late_attendance_approval_minutes",
    InstructionalCredit = "instructional_credit",
    InstructionalClock = "instructional_clock",
    LateAttendanceApprovalMessages = "late_attendance_approval_messages",
    BaseOnScheduleHourPlusCheckinBufferMinutes = "base_on_schedule_hour_plus_checkin_buffer_minutes",
    BaseOnScheduleHourPlusCheckoutBufferMinutes = "base_on_schedule_hour_plus_checkout_buffer_minutes",
    BaseOnScheduleCheckinBufferMinutesEnabled = "base_on_schedule_checkin_buffer_minutes_enabled",
    BaseOnScheduleCheckoutBufferMinutesEnabled = "base_on_schedule_checkout_buffer_minutes_enabled",
    EditDailyAttendanceByDateEnabled = "edit_daily_attendance_by_date_enabled",
    EnableHighSchoolInAdmissionsModule = "enable_high_school_in_admissions_module",
    EnablePartnerInAdmissionsModule = "enable_partner_in_admissions_module",
    DailyAttendanceBreakOption = "daily_attendance_break_option",
    DailyAttendanceBreakMinutes = "daily_attendance_break_minutes",
    FameAdvantageEnabled = "fame_advantage_enabled",
    FameFreedomEnabled = "fame_freedom_enabled",
    IsAttendanceModuleEnabled = "is_attendance_module_enabled",
    IsPracticalAutoRejectionEnabled = "is_practical_auto_rejection_enabled",
    PracticalAutoRejectionDays = "practical_auto_rejection_days",
    RejectionOfDailyAttendanceEnabled = "rejection_of_daily_attendance_enabled",
    RejectionOfDailyAttendanceDays = "rejection_of_daily_attendance_days",
    RejectionOfDailyAttendanceMessages = "rejection_of_daily_attendance_messages",
    LockDailyAttendanceEnabled = "lock_daily_attendance_enabled",
    LockDailyAttendanceDays = "lock_daily_attendance_days",
    LockDailyAttendanceMessages = "lock_daily_attendance_messages"
}

export type GeneralSetting = {
    field: GeneralSettingField
    value: string
    updatedAt: string
}

export const faStatusByOptions: Array<{id: FAStatusBy; name: string}> = [
    {id: FAStatusBy.AwardYear, name: "Award Year"},
    {id: FAStatusBy.Term, name: "Term"}
]

export const faAwardWorksheetTemplateOptions: Array<{id: FAAwardWorksheetTemplate; name: string}> = [
    {id: FAAwardWorksheetTemplate.Advanced, name: "Advanced"},
    {id: FAAwardWorksheetTemplate.Standard, name: "Standard"}
]

export enum AttendanceRoundingType {
    Classroom = "classroom",
    Daily = "daily"
}

export enum DailyAttendanceTimeClockTracking {
    AnyTime = "any_time",
    BaseOnScheduleHourOnly = "base_on_schedule_hour_only",
    BaseOnScheduleHourPlus = "base_on_schedule_hour_plus"
}

export enum DailyAttendanceBreakOption {
    AnyTime = "any_time",
    BreakMinutes = "break_minutes"
}

export enum AccommodatedHoursOption {
    ByShifts = "by_shifts",
    AnyTime = "any_time"
}

export type ListAttendanceRoundingPayload = {
    range?: {
        page: number
        pageSize: number
    }
    filter?: Partial<{
        type: AttendanceRoundingType
    }>
}

export type EditAttendanceRoundingPayload = {
    attendanceRoundingTables: Array<{
        roundingTableId?: number
        isAppliedAllCampus: boolean
        campusIds: number[]
        attendanceRoundingRanges: Array<AttendanceRoundingItem>
    }>
    specialAttendanceRoundingRanges: Array<AttendanceRoundingItem>
    type: AttendanceRoundingType
    enabled: boolean
}

export type AttendanceRoundingItem = {
    from: number
    to: number
    id?: number
    value: number
    createdAt?: string
}
